import React from 'react';
import { motion } from 'framer-motion';
import { MaskLines, HeroScrollFade, ParallaxBlock } from '../motion';
import HeroBackdrop from '../HeroBackdrop';
import { surfaceVariants, viewportOnce, containerVariants, itemVariants } from '../../lib/motion';

/** Hero shell: full-bleed wave video behind the headline. */
export const PgHero = ({ children, id }) => (
  <section id={id} className="pg-hero pg-grain">
    <HeroBackdrop />
    <div className="container" style={{ position: 'relative', zIndex: 1 }}>
      {children}
    </div>
  </section>
);

/** Headline whose lines rise out of their masks, one after another. */
export const PgTitle = ({ lines }) => (
  <MaskLines className="pg-h1" lines={lines} delay={0.12} />
);

/** Hero copy / CTA that fades up on load. */
export const PgIn = ({ children, delay = 0, className, style }) => (
  <motion.div
    className={className}
    style={style}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

/** Primary hero CTA with the spring press Ortexo uses. */
export const PgPill = ({ children, ...props }) => (
  <motion.a
    className="pg-pill"
    whileHover={{ scale: 1.045 }}
    whileTap={{ scale: 0.97 }}
    transition={{ type: 'spring', stiffness: 300, damping: 18 }}
    {...props}
  >
    {children}
  </motion.a>
);

/** The product surface panel rises into place with a touch of perspective. */
export const PgSurface = ({ children, id }) => (
  <div style={{ perspective: 1400 }}>
    <motion.div
      id={id}
      className="pg-surface"
      variants={surfaceVariants}
      initial="hidden"
      animate="visible"
      style={{ transformStyle: 'preserve-3d' }}
    >
      {children}
    </motion.div>
  </div>
);

/** Hero content that drifts away as the reader scrolls on. */
export const PgHeroInner = ({ children }) => (
  <HeroScrollFade>
    <div className="pg-hero-inner">{children}</div>
  </HeroScrollFade>
);

/** Split section — text half and panel half reveal together on scroll. */
export const PgSplit = ({ children, reverse = false }) => (
  <motion.div
    className={`pg-split${reverse ? ' rev' : ''}`}
    variants={containerVariants}
    initial="hidden"
    whileInView="visible"
    viewport={viewportOnce}
  >
    {children}
  </motion.div>
);

export const PgSplitText = ({ children }) => (
  <motion.div className="pg-split-text" variants={itemVariants}>
    {children}
  </motion.div>
);

export const PgSplitPanel = ({ children }) => (
  <motion.div className="pg-split-media is-panel" variants={itemVariants}>
    <ParallaxBlock distance={18} fade={false} scaleRange={[1, 1, 1]} style={{ width: '100%' }}>
      {children}
    </ParallaxBlock>
  </motion.div>
);

/** Feature list whose copper rules draw themselves in sequence. */
export const PgFeats = ({ items }) => (
  <div className="pg-feats">
    {items.map((text, i) => (
      <div key={i} className="pg-feat">
        <motion.span
          className="pg-feat-line"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={viewportOnce}
          transition={{ duration: 0.5, delay: 0.3 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
        />
        <span>{text}</span>
      </div>
    ))}
  </div>
);

/** Mini data panel: label plus key/value rows that tick in one by one. */
export const PgMini = ({ label, rows, children }) => (
  <motion.div
    className="pg-mini"
    variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } } }}
    initial="hidden"
    whileInView="visible"
    viewport={viewportOnce}
  >
    {label && (
      <motion.span
        className="pg-mini-label"
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      >
        {label}
      </motion.span>
    )}
    {rows &&
      rows.map((row) => (
        <motion.div
          key={row.k}
          className={`pg-mini-row${row.hi ? ' hi' : ''}`}
          variants={{
            hidden: { opacity: 0, x: 16 },
            visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 130, damping: 16 } },
          }}
        >
          <span className="k">{row.k}</span>
          <span className="v">{row.v}</span>
        </motion.div>
      ))}
    {children &&
      React.Children.map(children, (child) => (
        <motion.div
          variants={{
            hidden: { opacity: 0, x: 16 },
            visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 130, damping: 16 } },
          }}
        >
          {child}
        </motion.div>
      ))}
  </motion.div>
);

/** Full-bleed video break with the Ortexo parallax treatment. */
export const PgBreak = ({ src }) => (
  <section className="pg-break">
    <ParallaxBlock
      className="pg-break-media"
      distance={70}
      fade={false}
      scaleRange={[1.12, 1.02, 1.12]}
    >
      <video autoPlay muted loop playsInline preload="metadata">
        <source src={src} type="video/mp4" />
      </video>
    </ParallaxBlock>
  </section>
);

/** Closing CTA block, revealed on scroll. */
export const PgOutro = ({ children }) => (
  <motion.div
    variants={containerVariants}
    initial="hidden"
    whileInView="visible"
    viewport={viewportOnce}
    style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto' }}
  >
    {children}
  </motion.div>
);

export const PgOutroItem = ({ children, className, style, as = 'div' }) => {
  const Comp = motion[as] || motion.div;
  return (
    <Comp className={className} style={style} variants={itemVariants}>
      {children}
    </Comp>
  );
};
